import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../constants";
import { CONTACT_GROUPS } from "./helpers";
import ContactForm from "./ContactForm";

/**
 * ContactList — phone book tab. Search + group filter + import + paginated list.
 */
export default function ContactList() {
  const fileInputRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("All");
  const [isLoading, setIsLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (group !== "All") params.group = group;
      if (search) params.search = search;
      const res = await axios.get(`${API}/crm/contacts`, { params });
      setContacts(res.data.items);
    } catch {
      toast.error("Failed to load contacts");
    } finally {
      setIsLoading(false);
    }
  }, [group, search]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await axios.delete(`${API}/crm/contacts/${id}`);
      toast.success("Contact deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["csv", "vcf"].includes(ext)) { toast.error("Please select a .csv or .vcf file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      setIsLoading(true);
      const res = await axios.post(`${API}/crm/contacts/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.imported} contacts`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div data-testid="crm-phonebook">
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          className="input-dark flex-1" placeholder="Search contacts..."
          data-testid="contact-search"
        />
        <select value={group} onChange={(e) => setGroup(e.target.value)} className="input-dark w-40" data-testid="contact-group-filter">
          {CONTACT_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <input type="file" ref={fileInputRef} accept=".csv,.vcf" onChange={importCsv} className="hidden" data-testid="import-file-input" />
        <button onClick={() => fileInputRef.current?.click()} className="btn-secondary whitespace-nowrap text-sm" data-testid="import-contacts-btn">
          <svg className="w-4 h-4 inline mr-1.5 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
          Import
        </button>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary whitespace-nowrap" data-testid="add-contact-btn">
          + New Contact
        </button>
      </div>

      <div className="grid gap-3" data-testid="contact-list">
        {isLoading ? <div className="text-[#E8E4DC] text-center py-8">Loading...</div> :
          contacts.length === 0 ? <div className="text-[#E8E4DC] text-center py-12 card-glass">No contacts found. Add your first contact!</div> :
            contacts.map((c) => (
              <div key={c.id} className="card-glass p-4 flex items-center gap-4 hover:border-[#A3E635]/30" data-testid={`contact-card-${c.id}`}>
                <div className="w-12 h-12 rounded-full bg-[#A3E635]/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {c.photo_url ? <img src={c.attachments?.[0]?.thumb || c.photo_url} alt="" className="w-full h-full object-cover rounded-full" loading="lazy" /> :
                    <span className="text-[#A3E635] font-bold text-lg">{(c.first_name?.[0] || "").toUpperCase()}{(c.last_name?.[0] || "").toUpperCase()}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium truncate">{c.first_name} {c.last_name}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-[#E8E4DC]">{c.group}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    {c.emails?.[0]?.email && <span className="text-[#E8E4DC] text-sm truncate">{c.emails[0].email}</span>}
                    {c.phones?.[0]?.number && <span className="text-[#E8E4DC] text-sm">{c.phones[0].number}</span>}
                    {c.company && <span className="text-[#E8E4DC] text-sm">{c.company}</span>}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => { setEditing(c); setShowForm(true); }} className="text-[#E8E4DC] hover:text-[#A3E635] transition-colors p-1" data-testid={`edit-contact-${c.id}`}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                  </button>
                  <button onClick={() => remove(c.id)} className="text-[#E8E4DC] hover:text-red-400 transition-colors p-1" data-testid={`delete-contact-${c.id}`}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </div>
            ))}
      </div>

      <ContactForm
        open={showForm}
        initial={editing}
        onClose={() => { setShowForm(false); setEditing(null); }}
        onSaved={load}
      />
    </div>
  );
}
